import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { Button, PageHeader, PageLayout, Skeleton } from '@keboola/design';
import { ArrowLeft } from '@keboola/design/icons';

import { useCurrentVendor } from '../../../../auth/context';
import { QueryError } from '../../../../components/QueryError';
import { EditAppForm } from '../../../../features/apps/editForm/EditAppForm';
import { useUpdateApp, useVendorApp } from '../../../../features/vendors/queries';

export const Route = createFileRoute('/~/components/$appId/edit')({
  component: VendorComponentEditPage,
  head: () => ({ meta: [{ title: 'Edit component | Keboola Developer Portal' }] }),
});

function VendorComponentEditPage() {
  const { appId } = Route.useParams();
  const vendorId = useCurrentVendor();
  const navigate = useNavigate();
  const { data: app, isLoading, isError, error, refetch } = useVendorApp(vendorId, appId);
  const updateApp = useUpdateApp(vendorId);

  return (
    // `narrow` is the design width for forms; `full` stretches the ~40-field form across the window.
    <PageLayout maxWidth="narrow">
      <PageLayout.Header>
        <PageHeader.Detail
          title={app?.name ?? appId}
          description="How the component appears and behaves on the platform."
          breadcrumbs={
            <Button asChild variant="inline">
              <Link to="/~/components/$appId" params={{ appId }}>
                <ArrowLeft />
                Back to the component
              </Link>
            </Button>
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-96" />}

      {/* `titleAs="h2"`: the default `h3` would skip a level under the page header's `h1`. */}
      <QueryError
        isError={isError}
        hasData={app !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load this component"
        refreshTitle="Could not refresh this component"
        staleNote="Showing the version from the last successful load."
        titleAs="h2"
      />

      {app !== undefined && (
        <EditAppForm
          // Keyed: the route stays mounted across `$appId`, and an edited form ignores new
          // `defaultValues` — one component's values would reach another's `PATCH`.
          key={app.id}
          app={app}
          audience="vendor"
          onSave={(input) => updateApp.mutateAsync({ appId, input })}
          // The mutation already invalidated the cache, so the detail page shows the saved values.
          onSaved={() => void navigate({ to: '/~/components/$appId', params: { appId } })}
        />
      )}
    </PageLayout>
  );
}
